import { useState } from "react";
import * as Icons from "lucide-react";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Combobox } from "@/components/ui/combobox";
import { DualRangeSlider } from "@/components/ui/dual-range-slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Controller, useFormContext } from "react-hook-form";
import { defaultValues, FilterState } from "../(context)/filter";
import { useFormReset } from "@/hooks/use-form-reset";
import { COUNTRIES } from "@/constants/countries";
import { useQuery } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import { subYears, addYears } from "date-fns";

type Props = {
  onSearch?: () => void;
}

export const NewSearchFilter = ({
  onSearch
}:Props) => {
  const form = useFormContext<FilterState>()
  const reset = useFormReset(form);
  const [open, setopen] = useState(false);

  return (
    <AlertDialog open={open} onOpenChange={setopen}>
      <AlertDialogTrigger asChild>
        <Button variant="bright" shape="pill" size="2xl">
          <Icons.Search className="w-4 h-4" />
          Nieuwe zoekfilter
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="max-h-[95dvh] overflow-auto flex flex-col justify-between p-0 gap-0">
        <AlertDialogHeader className="p-4 flex items-center justify-center border-b relative">
          <AlertDialogTitle>Filters</AlertDialogTitle>
          <button
            onClick={() => {
              setopen(false);
            }}
            className="absolute right-4 top-1/2 -translate-y-1/2"
          >
            <Icons.X className="w-4 h-4" />
          </button>
        </AlertDialogHeader>
        <div className="h-full overflow-auto px-4 space-y-4">
          <Accordion type="multiple" defaultValue={["general-search"]}>
            <GeneralSearch />
            <CountryCompetition />
            <NameOptions />
            <PersonalData />
            {/* <PlayerProperties /> */}
            {/* <NationalTeamData /> */}
            {/* <ContractStatuses /> */}
          </Accordion>
        </div>
        <div className="flex justify-between w-full p-4 border-t">
          <Button onClick={()=>{ form.reset(defaultValues) }} variant="ghost">Alles wissen</Button>
          <Button onClick={()=>{
            onSearch?.();
            setopen(false);
          }}>Spelers weergeven</Button>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
};

const GeneralSearch = () => {
  const { control } = useFormContext<FilterState>()

  return (
    <AccordionItem value="general-search" className="border-b">
      <AccordionTrigger>
        <p className="text-base font-medium">Algemeen zoekveld</p>
      </AccordionTrigger>
      <AccordionContent>
        <Controller 
          control={control}
          name="search"
          render={({ field }) => (
            <Input 
              {...field} 
              shape="pill" 
              className="h-10"
              placeholder="Zoek naar spelers, teams, competities, ID's..."
              startContent={<Icons.Search className="w-4 h-4" />}
            />
          )}
        />
      </AccordionContent>
    </AccordionItem>
  )
}

const CountryCompetition = () => {
  const { trpc } = useTRPC();
  const { control, watch } = useFormContext<FilterState>()
  const country = watch('competition.country')
  const competition = watch('competition.competition_id')

  const { data:competitions } = useQuery({
    queryKey: ['competitions', country],
    queryFn: async () => {
      const competitions = await trpc.competitions.search.query({
        country: country
      })
      return competitions;
    },
    enabled: !!country
  })
  
  const { data:clubs } = useQuery({
    queryKey: ['clubs', competition],
    queryFn: async () => {
      const clubs = await trpc.clubs.search.query({
        competition: {
          competition_id: competition
        }
      })
      return clubs;
    },
    enabled: !!competition
  })

  return (
    <AccordionItem value="country-competition" className="border-b">
      <AccordionTrigger>
        <p className="text-base font-medium">Club</p>
      </AccordionTrigger>
      <AccordionContent>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Controller
              control={control}
              name="competition.country"
              render={({ field }) => (
                <Combobox 
                  value={field.value}
                  onValueChange={(v)=>{
                    field.onChange(v);
                  }}
                  options={COUNTRIES.map(c => ({ value:c.name, label: c.name }))}
                  placeholder="Land" 
                  className="rounded-full"
                />
              )}
            />
          </div>
          <div>
            <Controller
              control={control}
              name="competition.competition_id"
              render={({ field }) => (
                <Combobox 
                  value={field.value}
                  onValueChange={(v)=>{
                    field.onChange(v);
                  }}
                  disabled={!country || competitions?.competitions?.length === 0}
                  options={competitions?.competitions.map(c => ({ value:`${c.id}`, label: `${c.name}` })) || []}
                  placeholder="Competitie" 
                  className="rounded-full"
                />
              )}
            />
          </div>
          <div>
          <Controller
              control={control}
              name="competition.club_id"
              render={({ field }) => (
                <Combobox 
                  value={field.value}
                  onValueChange={(v)=>{
                    field.onChange(v);
                  }}
                  disabled={!competition || clubs?.clubs?.length === 0}
                  options={clubs?.clubs.map(c => ({ value:`${c.id}`, label: `${c.name}` })) || []}
                  placeholder="Club" 
                  className="rounded-full"
                />
              )}
            />
          </div>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};

const NameOptions = () => {
  const { control } = useFormContext<FilterState>()
  return (
    <AccordionItem value="name-options" className="border-b">
      <AccordionTrigger>
        <p className="text-base font-medium">Naam opties</p>
      </AccordionTrigger>
      <AccordionContent>
        <div className="flex flex-col gap-2">
          <div className="grid grid-cols-2">
            <div className="flex items-center">
              <p>Voornaam</p>
            </div>
            <Controller 
              control={control}
              name="nameOptions.first_name"
              render={({ field }) => (
                <Input shape="pill" {...field} />
              )}
            />
          </div>
          <div className="grid grid-cols-2">
            <div className="flex items-center">
              <p>Achternaam</p>
            </div>
            <Controller 
              control={control}
              name="nameOptions.last_name"
              render={({ field }) => (
                <Input shape="pill" {...field} />
              )}
            />
          </div>
          {/* <div className="grid grid-cols-2">
            <div className="flex items-center">
              <p>Pseudoniem</p>
            </div>
            <Controller 
              control={control}
              name="nameOptions.pseudonym"
              render={({ field }) => (
                <Input shape="pill" {...field} />
              )}
            />
          </div> */}
          <div className="grid grid-cols-2">
            <div className="flex items-center">
              <p>Volledige naam</p>
            </div>
            <Controller 
              control={control}
              name="nameOptions.full_name"
              render={({ field }) => (
                <Input shape="pill" {...field} />
              )}
            />
          </div>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};

const PersonalData = () => {
  const { control } = useFormContext<FilterState>()
  return (
    <AccordionItem value="personal-data" className="border-b">
      <AccordionTrigger>
        <p className="text-base font-medium">Persoonlijke gegevens</p>
      </AccordionTrigger>
      <AccordionContent>
        <div className="flex flex-col gap-2">
          <div className="grid grid-cols-2">
            <div className="flex items-center">
              <p>Geboorteplaats</p>
            </div>
            <Controller 
              control={control}
              name="personalData.place_of_birth.city"
              render={({ field }) => (
                <Input shape="pill" {...field} />
              )}
            />
          </div>
          {/* <div className="grid grid-cols-2">
            <div className="flex items-center">
              <p>Geboorteland</p>
            </div>
            <Controller
              control={control}
              name="personalData.place_of_birth.country"
              render={({ field }) => (
                <Combobox 
                  value={field.value}
                  onValueChange={field.onChange}
                  options={COUNTRIES.map(c => ({ value:c.name, label: c.name }))}
                  placeholder="" 
                  className="rounded-full"
                />
              )}
            />
          </div> */}
          <div className="grid grid-cols-2">
            <div className="flex items-center">
              <p>Nationaliteit</p>
            </div>
            <Controller
              control={control}
              name="personalData.citizenship"
              render={({ field }) => (
                <Combobox 
                  value={field.value}
                  onValueChange={field.onChange}
                  options={COUNTRIES.map(c => ({ value:c.name, label: c.name }))}
                  placeholder="" 
                  className="rounded-full"
                />
              )}
            />
          </div>
          <div className="grid grid-cols-2">
            <div className="flex items-center">
              <p>Tweede nationaliteit</p>
            </div>
            <Controller
              control={control}
              name="personalData.other_citizenship"
              render={({ field }) => (
                <Combobox 
                  value={field.value}
                  onValueChange={field.onChange}
                  options={COUNTRIES.map(c => ({ value:c.name, label: c.name }))}
                  placeholder="" 
                  className="rounded-full"
                />
              )}
            />
          </div>

          {/* <div className="grid grid-cols-2 opacity-20">
            <div className="flex items-center">
              <p>Speler uit voetbalbond</p>
            </div>
            <Combobox options={[]} className="rounded-full" />
          </div> */}

          <div className="grid grid-cols-2">
            <div className="flex items-center">
              <p>Geboortedatum</p>
            </div>
            <Controller 
                control={control}
                name="personalData.date_of_birth"
                render={({ field }) => (
                  <Input {...field} shape="pill" type="date"/>
                )}
              />
          </div>
          <div className="grid grid-cols-2">
            <div className="flex items-center">
              <p>Leeftijd</p>
            </div>

            <div className="flex items-center gap-2">
              <Controller 
                control={control}
                name="personalData.age_min"
                render={({ field }) => (
                  <Input value={field.value} onChange={(event) => field.onChange(+event.target.value)} shape="pill" type="number"/>
                )}
              />
              <Icons.Minus className="w-4 h-4 min-w-4"/>
              <Controller 
                control={control}
                name="personalData.age_max"
                render={({ field }) => (
                  <Input value={field.value} onChange={(event) => field.onChange(+event.target.value)} shape="pill" type="number"/>
                )}
              />
            </div>
          </div>
          <div className="grid grid-cols-2">
            {/*
            <div className="flex items-center">
              <p>Lengte</p>
            </div>

             <div className="flex items-center gap-2">
              <Controller 
                control={control}
                name="personalData.height_min"
                render={({ field }) => (
                  <Input value={field.value} onChange={(event) => field.onChange(+event.target.value)} shape="pill" type="number" endContent={"cm"} min={100} max={250}/>
                )}
                
              />
              <Icons.Minus className="w-4 h-4 min-w-4"/>
              <Controller 
                control={control}
                name="personalData.height_max"
                render={({ field }) => (
                  <Input value={field.value} onChange={(event) => field.onChange(+event.target.value)} shape="pill" type="number" endContent={"cm"} min={100} max={250}/>
                )}
              />
            </div> */}
          </div>
        </div>
      </AccordionContent>
    </AccordionItem>
  )
}

// const PlayerProperties = () => {
//   const { control, watch, setValue } = useFormContext<FilterState>()

//   // Slider range from 0 to 100
//   const handleSliderChange = ([pMin, pMax]: [number, number]) => {
//     const realMin = Math.round((pMin / 100) ** 2 * 200_000_000);
//     const realMax = Math.round((pMax / 100) ** 2 * 200_000_000);
//     setValue("playerData.marketvalue_min", realMin);
//     setValue("playerData.marketvalue_max", realMax);
//   };

//   const realMin = watch("playerData.marketvalue_min") ?? 0;
//   const realMax = watch("playerData.marketvalue_max") ?? 200_000_000;

//   // Convert real values back to slider percent scale
//   const percentMin = Math.sqrt(realMin / 200_000_000) * 100;
//   const percentMax = Math.sqrt(realMax / 200_000_000) * 100;

//   const positionOptions = [
//     { label: "Goalkeeper", value: "Goalkeeper" },
//     { label: "Defender", value: "Defender" },
//     { label: "Centre-Back", value:"Centre-Back"},
//     { label: "Left-Back", value:"Left-Back"},
//     { label: "Right-Back", value:"Right-Back"},
//     { label: "Defensive Midfield", value:"Defensive Midfield"},
//     { label: "Midfielder", value:"Midfielder"},
//     { label: "Attacking Midfield", value:"Attacking Midfield"},
//     { label: "Central Midfield", value:"Central Midfield"},
//     { label: "Right Midfield", value:"Right Midfield"},
//     { label: "Left Winger", value:"Left Winger"},
//     { label: "Right Winger", value:"Right Winger"},
//     { label: "Centre-Forward", value:"Centre-Forward"},
//   ]

//   return (
//     // <AccordionItem value="player-properties" className="border-b">
//     //   <AccordionTrigger>
//     //     <p className="text-base font-medium">Speler eigenschappen</p>
//     //   </AccordionTrigger>
//     //   <AccordionContent className="space-y-4">
//     //     {/* <div className="space-y-2">
//     //       <p>Marktwaarde</p>

//     //       <DualRangeSlider
//     //         value={[percentMin, percentMax]}
//     //         onValueChange={handleSliderChange}
//     //         min={0}
//     //         max={100}
//     //         step={1}
//     //       />

//     //       <div className="flex items-center justify-between gap-2">
//     //         <div>
//     //           <p className="text-xs text-foreground/50 text-center">Minimum</p>
//     //           <Controller 
//     //             control={control}
//     //             name="playerData.marketvalue_min"
//     //             render={({ field }) => (
//     //               <Input value={field.value} onChange={(event) => field.onChange(+event.target.value)} shape="pill" type="number" startContent={"€"}/>
//     //             )}
//     //           />
//     //         </div>

//     //         <div>
//     //           <p className="text-xs text-foreground/50 text-center">Maximum</p>
//     //           <Controller 
//     //             control={control}
//     //             name="playerData.marketvalue_max"
//     //             render={({ field }) => (
//     //               <Input value={field.value} onChange={(event) => field.onChange(+event.target.value)} shape="pill" type="number" startContent={"€"}/>
//     //             )}
//     //           />
//     //         </div>
//     //       </div>
//     //     </div> */}
//     //     {/* <div className="grid grid-cols-3 gap-3">
//     //       <div>
//     //         <Controller 
//     //           control={control}
//     //           name="playerData.position"
//     //           render={({ field }) => (
//     //             <Combobox options={positionOptions} placeholder="Positie" className="rounded-full" value={field.value} onValueChange={(value)=>field.onChange(value)}/>
//     //           )}
//     //         />
//     //       </div>
//     //       <div>
//     //         <Controller 
//     //           control={control}
//     //           name="playerData.main_position"
//     //           render={({ field }) => (
//     //             <Combobox options={positionOptions} placeholder="Hoofd positie" className="rounded-full" value={field.value} onValueChange={(value)=>field.onChange(value)}/>
//     //           )}
//     //         />
//     //       </div>
//     //       <div>
//     //         <Controller 
//     //           control={control}
//     //           name="playerData.other_position"
//     //           render={({ field }) => (
//     //             <Combobox options={positionOptions} placeholder="Andere positie" className="rounded-full" value={field.value} onValueChange={(value)=>field.onChange(value)}/>
//     //           )}
//     //         />
//     //       </div>
//     //     </div> */}
//     //     <div className="flex flex-col gap-2">
//     //       {/* <div className="grid grid-cols-2">
//     //         <div className="flex items-center">
//     //           <p>Rugnummer</p>
//     //         </div>
//     //         <Controller 
//     //           control={control}
//     //           name="playerData.jersey_number"
//     //           render={({ field }) => (
//     //             <Input value={field.value} onChange={(event) => field.onChange(event.target.value)} shape="pill" startContent={"#"} />
//     //           )}
//     //         />
//     //       </div> */}
//     //       {/* <div className="grid grid-cols-2">
//     //         <div className="flex items-center">
//     //           <p>Aanvoerder</p>
//     //         </div>
//     //         <Select defaultValue="nvt">
//     //           <SelectTrigger className="rounded-full w-full">
//     //             <SelectValue />
//     //           </SelectTrigger>
//     //           <SelectContent>
//     //             <SelectItem value="nvt">Maakt niet uit</SelectItem>
//     //             <SelectItem value="true">Ja</SelectItem>
//     //             <SelectItem value="false">Nee</SelectItem>
//     //           </SelectContent>
//     //         </Select>
//     //       </div> */}
//     //       {/* <div className="grid grid-cols-2">
//     //         <div className="flex items-center">
//     //           <p>Einde van het contract in jaar</p>
//     //         </div>
//     //         <Controller 
//     //           control={control}
//     //           name="playerData.contract_expiration_year"
//     //           render={({ field }) => (
//     //             <Select
//     //               value={`${field.value}`}
//     //               onValueChange={(v)=>{
//     //                 field.onChange(parseInt(v));
//     //               }}
//     //             >
//     //               <SelectTrigger className="rounded-full w-full">
//     //                 <SelectValue />
//     //               </SelectTrigger>
//     //               <SelectContent>
//     //                 {Array(50).fill('').map((_,i)=>(
//     //                   <SelectItem key={i} value={`${new Date().getFullYear()+i}`}>{new Date().getFullYear()+i}</SelectItem>
//     //                 ))}
//     //               </SelectContent>
//     //             </Select>
//     //           )}
//     //         />
//     //       </div> */}
//     //       {/* <div className="grid grid-cols-2">
//     //         <div className="flex items-center">
//     //           <p>Competitie</p>
//     //         </div>
//     //         <Combobox options={[]} placeholder="" className="rounded-full"/>
//     //       </div>
//     //       <div className="grid grid-cols-2">
//     //         <div className="flex items-center">
//     //           <p>Devisie</p>
//     //         </div>
//     //         <Combobox options={[]} placeholder="" className="rounded-full"/>
//     //       </div> 
//     //       <div className="grid grid-cols-2">
//     //         <div className="flex items-center">
//     //           <p>Speelt voor club uit land</p>
//     //         </div>
//     //         <Controller
//     //           control={control}
//     //           name="playerData.club_nationality"
//     //           render={({ field }) => (
//     //             <Combobox 
//     //               value={field.value}
//     //               onValueChange={(v)=>{
//     //                 field.onChange(v);
//     //               }}
//     //               options={COUNTRIES.map(c => ({ value:c.name, label: c.name }))}
//     //               placeholder="Land" 
//     //               className="rounded-full"
//     //             />
//     //           )}
//     //         />
//     //       </div>
//     //       */}
//     //     </div>
//     //   </AccordionContent>
//     // </AccordionItem>
//   );
// };

const NationalTeamData = () => {
  return (
    <AccordionItem value="national-team-data" className="border-b">
      <AccordionTrigger>
        <p className="text-base font-medium">Nationale teamgegevens</p>
      </AccordionTrigger>
      <AccordionContent>
        <div className="flex flex-col gap-2">
          <div className="grid grid-cols-2">
            <div className="flex items-center">
              <p>A-international</p>
            </div>
            <div className="flex items-center justify-end">
              <Switch />
            </div>
          </div>
          <div className="grid grid-cols-2">
            <div className="flex items-center">
              <p>Aantal optredens</p>
            </div>

            <div className="flex items-center gap-2">
              <Input shape="pill" type="number"/>
              <Icons.Minus className="w-4 h-4 min-w-4"/>
              <Input shape="pill" type="number"/>
            </div>
          </div>
        </div>
      </AccordionContent>
    </AccordionItem>
  )
}

const ContractStatuses = () => {
  return (
    <AccordionItem value="contract-states" className="border-b">
      <AccordionTrigger>
        <p className="text-base font-medium">Contract status</p>
      </AccordionTrigger>
      <AccordionContent>
        <div className="flex flex-col gap-2">
          <div className="grid grid-cols-2">
            <div className="flex items-center">
              <p>Spelers met een nieuw contract uitsluiten</p>
            </div>
            <div className="flex items-center justify-end">
              <Switch />
            </div>
          </div>
          <div className="grid grid-cols-2">
            <div className="flex items-center">
              <p>Alleen actieve spelers beschouwen</p>
            </div>
            <div className="flex items-center justify-end">
              <Switch />
            </div>
          </div>
          <div className="grid grid-cols-2">
            <div className="flex items-center">
              <p>Alleen spelers zonder club beschouwen</p>
            </div>
            <div className="flex items-center justify-end">
              <Switch />
            </div>
          </div>
          <div className="grid grid-cols-2">
            <div className="flex items-center">
              <p>Huurspelers uitsluiten</p>
            </div>
            <div className="flex items-center justify-end">
              <Switch />
            </div>
          </div>
        </div>
      </AccordionContent>
    </AccordionItem>
  )
}